import { AdminOperatorRunStep } from "@domain/ai_operator"
import useTranslate from "@hooks/use_translate"
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined"
import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material"
import { useState } from "react"

import JsonBox from "./JsonBox"
import { formatMs, formatUsd } from "./operatorRun.utils"

type Field = "input" | "output" | "system"

type Props = {
  step: AdminOperatorRunStep
  bar: { left: number; width: number }
  expanded: boolean
  onToggle: () => void
}

function Details({ step }: { step: AdminOperatorRunStep }) {
  const { translate } = useTranslate()
  const isModel = step.kind === "MODEL"
  const fields = (
    [
      [
        "input",
        isModel ? "ai_operator_model_input" : "ai_operator_tool_params"
      ],
      [
        "output",
        isModel ? "ai_operator_model_output" : "ai_operator_tool_result"
      ],
      ["system", "ai_operator_system_prompt"]
    ] as const
  ).filter(([field]) => step[field] !== undefined && step[field] !== null)
  const [field, setField] = useState<Field | undefined>(fields[0]?.[0])

  const facts = [
    step.prompt?.name,
    step.prompt &&
      `${translate("ai_operator_layer")}: ${
        step.prompt.layer ?? translate("ai_operator_layer_base")
      }`,
    step.prompt?.schema && `schema: ${step.prompt.schema}`,
    step.prompt?.maxTokens && `max: ${step.prompt.maxTokens}`,
    step.prompt?.images &&
      `${translate("ai_operator_images")}: ${step.prompt.images}`,
    step.usage &&
      `${step.usage.inputTokens}→${step.usage.outputTokens} token${
        step.usage.cachedInputTokens
          ? ` (cache ${step.usage.cachedInputTokens})`
          : ""
      }`,
    step.usage && formatUsd(step.usage.costUsd),
    `+${formatMs(step.atMs)}`
  ].filter(Boolean)

  return (
    <Stack gap={1.5} sx={{ px: 2, pb: 2, pt: 0.5 }}>
      {step.error ? (
        <Alert severity="error" sx={{ py: 0 }}>
          {step.error}
        </Alert>
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {facts.join(" · ")}
      </Typography>
      {fields.length ? (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={field}
          onChange={(_event, value: Field | null) => value && setField(value)}
        >
          {fields.map(([key, label]) => (
            <ToggleButton
              key={key}
              value={key}
              sx={{ textTransform: "none", py: 0.25, px: 1.5 }}
            >
              {translate(label)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}
      {field ? (
        <JsonBox
          key={field}
          value={step[field]}
          truncated={step.truncated?.includes(field)}
          maxHeight={520}
          language={field === "system" ? "markdown" : undefined}
        />
      ) : null}
    </Stack>
  )
}

export default function OperatorStepRow({
  step,
  bar,
  expanded,
  onToggle
}: Props) {
  const isModel = step.kind === "MODEL"
  const color = step.ok ? "text.secondary" : "error.main"
  const Icon = !step.ok
    ? ErrorOutlineIcon
    : isModel
      ? SmartToyOutlinedIcon
      : BuildOutlinedIcon

  return (
    <Box sx={{ bgcolor: expanded ? "action.hover" : undefined }}>
      <ButtonBase
        onClick={onToggle}
        sx={{
          width: "100%",
          px: 2,
          py: 1.25,
          gap: 1.5,
          justifyContent: "flex-start",
          textAlign: "left"
        }}
      >
        <Icon sx={{ fontSize: 18, color }} />
        <Typography
          variant="body2"
          fontWeight={600}
          color={step.ok ? "text.primary" : "error.main"}
          noWrap
          flexShrink={0}
        >
          {step.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap flex={1}>
          {step.prompt
            ? `${step.prompt.model}${step.prompt.reasoning ? ` · ${step.prompt.reasoning}` : ""}`
            : ""}
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: 140,
            height: 4,
            flexShrink: 0,
            bgcolor: "divider",
            borderRadius: 2
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: `${bar.left}%`,
              width: `${bar.width}%`,
              top: 0,
              bottom: 0,
              bgcolor: step.ok
                ? isModel
                  ? "primary.main"
                  : "success.main"
                : "error.main",
              borderRadius: 2
            }}
          />
        </Box>
        <Typography
          variant="caption"
          fontWeight={600}
          width={52}
          textAlign="right"
          flexShrink={0}
        >
          {formatMs(step.durationMs)}
        </Typography>
      </ButtonBase>
      <Collapse in={expanded} unmountOnExit>
        <Details step={step} />
      </Collapse>
    </Box>
  )
}
